import React, { useState, useEffect } from "react";
import { TextField, Typography, CircularProgress } from "@mui/material";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import ButtonComponent from "components/common/ButtonComponent";
import CustomDialog from "components/common/DialogContainer";
import { selectIsSavingGoal } from "store/kpi-config/KpiConfigSelectors";
import {
  SetGoalContentWrapper,
  GoalInputContainer,
  GoalLabel,
  GoalActionsWrapper,
  GoalLoadingOverlay,
} from "./PerformanceStyles";

interface SetGoalModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (goal: number) => void;
  currentGoal?: number;
}

const SetGoalModal: React.FC<SetGoalModalProps> = ({
  open,
  onClose,
  onSave,
  currentGoal,
}) => {
  const intl = useIntl();
  const isSaving = useSelector(selectIsSavingGoal);
  const [goalValue, setGoalValue] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open) {
      setGoalValue(currentGoal !== undefined ? String(currentGoal) : "");
      setError("");
    }
  }, [open, currentGoal]);

  const handleGoalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty, numbers, decimals, and negative sign
    if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
      setGoalValue(value);
      setError("");
    }
  };

  const handleSave = () => {
    if (goalValue.trim() === "") {
      setError(
        intl.formatMessage({
          id: "setGoalModal.emptyError",
          defaultMessage: "Please enter a goal value",
        }),
      );
      return;
    }

    const numericValue = parseFloat(goalValue);
    if (isNaN(numericValue)) {
      setError(
        intl.formatMessage({
          id: "setGoalModal.invalidError",
          defaultMessage: "Please enter a valid number",
        }),
      );
      return;
    }
    onSave(numericValue);
  };

  const modalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "setGoalModal.title",
          defaultMessage: "Set Goal",
        })}
      </Typography>
    );
  };

  const modalContent = () => {
    return (
      <div style={{ position: "relative" }}>
        {isSaving && (
          <GoalLoadingOverlay>
            <CircularProgress size={32} />
          </GoalLoadingOverlay>
        )}
        <SetGoalContentWrapper>
          <GoalInputContainer>
            <GoalLabel>
              {intl.formatMessage({
                id: "setGoalModal.goalLabel",
                defaultMessage: "Goal",
              })}
            </GoalLabel>
            <TextField
              variant="outlined"
              size="small"
              value={goalValue}
              onChange={handleGoalChange}
              error={!!error}
              helperText={error}
              autoFocus
              disabled={isSaving}
              inputProps={{
                "aria-label": "goal value",
              }}
              sx={{ flex: 1 }}
            />
          </GoalInputContainer>
        </SetGoalContentWrapper>
      </div>
    );
  };

  const modalActions = () => {
    return (
      <GoalActionsWrapper>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
        >
          {intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
        >
          {intl.formatMessage({
            id: "general.ok",
            defaultMessage: "OK",
          })}
        </ButtonComponent>
      </GoalActionsWrapper>
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      title={modalTitle()}
      actions={modalActions()}
      maxWidth="xs"
      fullWidth
    >
      {modalContent()}
    </CustomDialog>
  );
};

export default SetGoalModal;
